import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight } from "lucide-react";
import { LOGO_URL, EASE } from "../data";

const LINKS = [
  { label: "Products", id: "products" },
  { label: "For Consumers", id: "consumers" },
  { label: "For Enterprise", id: "business" },
  { label: "Our Ecosystem", id: "ecosystem" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/75 border-b"
      style={{ borderColor: "var(--border-light)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center"
        >
          <img src={LOGO_URL} alt="Straight" className="h-7 md:h-9 w-auto" />
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-sm font-medium text-black/50 hover:text-black transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            data-testid="nav-consumer-btn"
            onClick={() => scrollTo("products")}
            className="hidden sm:inline-flex items-center gap-1 rounded-full border text-black text-sm font-semibold px-5 py-2.5 hover:bg-black/5 transition-colors duration-300"
            style={{ borderColor: "rgba(0,0,0,0.15)" }}
          >
            Consumer <ChevronRight size={15} />
          </button>
          <button
            data-testid="nav-enterprise-btn"
            onClick={() => scrollTo("products")}
            className="hidden sm:inline-flex items-center gap-1 rounded-full bg-black text-white text-sm font-semibold px-5 py-2.5 hover:scale-105 transition-transform duration-300"
          >
            Enterprise <ChevronRight size={15} />
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-black p-2"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="md:hidden overflow-hidden border-t bg-white"
            style={{ borderColor: "var(--border-light)" }}
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => scrollTo(l.id)}
                  className="text-left text-lg font-medium text-black/80"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-consumer-btn"
                onClick={() => scrollTo("products")}
                className="mt-2 rounded-full border text-black text-sm font-semibold px-6 py-3"
                style={{ borderColor: "rgba(0,0,0,0.15)" }}
              >
                Consumer
              </button>
              <button
                data-testid="nav-mobile-enterprise-btn"
                onClick={() => scrollTo("products")}
                className="rounded-full bg-black text-white text-sm font-semibold px-6 py-3"
              >
                Enterprise
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
